import _ from "lodash"
import pointInPolygon from "@turf/boolean-point-in-polygon"
import { getCenterFromGeometry } from "utils/geometry"
import { sendCaptureExceptionToSentry } from "../../../utils/sentry"

// Used for clean up features removing missing feature from an Array of Features
const sanitizedFeatures = (features) => {
  return _.compact(features)
}

const bindAnchor = (feature, anchor) => {
  if (anchor) {
    feature.properties.anchor = anchor

    // TODO: Check if this is still used?
    feature.properties.ordinal = anchor?.properties?.unit?.properties?.ordinal
    feature.properties.level_name =
      anchor?.properties?.unit?.properties?.level?.properties?.name.en
    feature.properties.level_id =
      anchor?.properties?.unit?.properties?.level?.id
  }
}

const bindLevel = (feature, level) => {
  if (level) {
    feature.properties.level = level
    feature.properties.ordinal = level.properties.ordinal
  }
}

const bindLocalCategories = (feature, localCategories) => {
  feature.properties.local_categories = localCategories

  // Attach Parent Categories
  const parentCategories = localCategories.filter(
    (cat) => cat?.properties.parent_id === null
  )
  feature.properties.local_parent_categories = parentCategories
}

const bindGroups = (feature, groups) => {
  feature.properties.groups = groups
}

const bindUnit = (feature, unit) => {
  feature.properties.unit = unit
}

const bindKiosk = (feature, kiosk) => {
  feature.properties.kiosk = kiosk
}

const bindUnits = (feature, units) => {
  feature.properties.units = units

  // Default level at units[0]
  // TODO: Check if this is still used?
  const [defaultUnit] = units
  feature.properties.ordinal = defaultUnit.properties.ordinal
  feature.properties.level_name =
    defaultUnit.properties.level?.properties?.name.en
}

const bindOccupant = (feature, occupant) => {
  if (occupant) {
    feature.properties.occupant = occupant
  }
}

const bindFeature = (feature, childFeature) => {
  if (childFeature) {
    feature.properties.feature = childFeature
  }
}

const bindOrigin = (feature, origin) => {
  feature.properties.origin = origin
}

const bindDestination = (feature, destination) => {
  if (destination) {
    feature.properties.destination = destination
  }
}

const bindIntermediary = (feature, intermediary = null) => {
  feature.properties.intermediary = intermediary
}

const bindPromotions = (feature, promotions) => {
  feature.properties.promotions = promotions
}

const bindPrivileges = (feature, privileges) => {
  feature.properties.privileges = privileges
}

const bindSection = (feature, section) => {
  feature.properties.section = section
}

const bindVenue = (feature, venue) => {
  feature.properties.venue = venue
}

const findFeatureInSection = (feature, sections = []) => {
  const { geometry } = feature
  const center = getCenterFromGeometry(geometry)
  return sections.find((section) => pointInPolygon(center, section))
}

export const prepareMapDecorations = (mapDecorations) => {
  const returnArray = mapDecorations.reduce((acc, decoration) => {
    const decorations = decoration.geometries.map(
      (decorationGeometry, index) => {
        const {
          geometry,
          options,
          properties: decorationProperties,
        } = decorationGeometry
        const { symbol } = options
        const id = decoration.id + `-${index}`
        const properties = { symbol, ...decorationProperties }

        return { id, geometry, properties }
      }
    )
    return [...acc, ...decorations]
  }, [])

  return returnArray
}

export const prepareFeatures = (venueData) => {
  const {
    amenities = [],
    anchors = [],
    details = [],
    events = [],
    fixtures = [],
    kiosks = [],
    occupants = [],
    openings = [],
    privileges = [],
    promotions = [],
    relationships = [],
    sections = [],
    levels = [],
    units = [],
    features: featureCollection = [],
  } = venueData

  // Store feature in objects for easy find by id
  const featuresObj = featureCollection.reduce(
    (acc, feature) => ({ ...acc, [feature.id]: feature }),
    {}
  )

  const findOne = (id) => {
    if (!id) return null
    try {
      if (!featuresObj[id])
        throw new Error(`[preparing] Cannot find feature with id ${id}`)
      return featuresObj[id]
    } catch (err) {
      console.log(err)
      return null
    }
  }

  // Prepare Levels
  levels.forEach((feature) => {
    const { venue_id } = feature.properties
    const venue = findOne(venue_id)
    bindVenue(feature, venue)
    // TODO: bindAddress(level)
    // TODO: bindBuildings(level)
  })

  // Prepare Sections
  sections.forEach((feature) => {
    const { level_id, venue_id } = feature.properties
    const level = findOne(level_id)
    const venue = findOne(venue_id)
    bindLevel(feature, level)
    bindVenue(feature, venue)
    // TODO: bindAddress()
    // TODO: bindCorrelation()
    // TODO: bindParent()
  })

  const sectionsByOrdinal = _(sections)
    .filter((feature) => feature.properties.category === "retail")
    .groupBy((feature) => feature.properties.ordinal)
    .value()

  // Prepare Units
  units.forEach((feature) => {
    const { level_id, venue_id } = feature.properties
    const level = findOne(level_id)
    const venue = findOne(venue_id)
    bindLevel(feature, level)
    const section = findFeatureInSection(
      feature,
      sectionsByOrdinal[feature.properties.ordinal]
    )
    bindSection(feature, section)
    bindVenue(feature, venue)

    try {
      if (_.isNil(level_id) || _.isNil(_.get(feature, "properties.level")))
        throw new Error(
          `A level field has no defined on an unit feature (${feature?.id}).`
        )
    } catch (error) {
      sendCaptureExceptionToSentry({
        error,
        level: "warning",
        transactionName: `A level field has no defined.`,
      })
    }
  })

  // Prepare Amenities
  amenities.forEach((feature) => {
    const { unit_ids, venue_id } = feature.properties
    const units = sanitizedFeatures(unit_ids.map(findOne))
    const venue = findOne(venue_id)
    bindUnits(feature, units)
    bindVenue(feature, venue)
    // TODO: bindAddress(feature)
    // TODO: bindCorrelation(feature)
  })

  // Prepare Anchors
  anchors.forEach((feature) => {
    const { unit_id } = feature.properties
    const locatedUnit = findOne(unit_id)
    bindUnit(feature, locatedUnit)
    bindLevel(feature, locatedUnit?.properties?.level)
    const section = findFeatureInSection(
      feature,
      sectionsByOrdinal[feature.properties.ordinal]
    )
    bindSection(feature, section)
    // TODO: bindAddress(anchor)
    try {
      if (_.isNil(unit_id) || _.isNil(_.get(feature, "properties.unit")))
        throw new Error(
          `A unit field has no defined on an anchor feature (${feature?.id}).`
        )
    } catch (error) {
      sendCaptureExceptionToSentry({
        error,
        level: "warning",
        transactionName: `A unit field has no defined.`,
      })
    }
  })

  // Prepare Details
  details.forEach((feature) => {
    const { level_id } = feature.properties
    bindLevel(feature, findOne(level_id))
  })

  // Prepare Fixtures
  fixtures.forEach((feature) => {
    const { anchor_id, level_id, venue_id } = feature.properties
    const level = findOne(level_id)
    const venue = findOne(venue_id)
    bindAnchor(feature, findOne(anchor_id))
    bindLevel(feature, level)
    bindVenue(feature, venue)
  })

  // Prepare Kiosks
  kiosks.forEach((feature) => {
    const { anchor_id, level_id, venue_id } = feature.properties
    const level = findOne(level_id)
    const anchor = findOne(anchor_id)
    const venue = findOne(venue_id)
    bindAnchor(feature, anchor)
    bindLevel(feature, level)
    bindVenue(feature, venue)
    const section = findFeatureInSection(
      feature,
      sectionsByOrdinal[feature.properties.ordinal]
    )
    bindSection(feature, section)

    try {
      if (_.isNil(level_id) || _.isNil(_.get(feature, "properties.level")))
        throw new Error(
          `A level field has no defined on a kiosk feature (${feature?.id}).`
        )
    } catch (error) {
      sendCaptureExceptionToSentry({
        error,
        level: "warning",
        transactionName: `A level field has no defined.`,
      })
    }
  })

  // Prepare Occupants
  occupants.forEach((feature) => {
    const {
      anchor_id,
      local_category_ids,
      kiosk_id,
      promotion_ids,
      privilege_ids,
      group_ids = [],
      venue_id,
    } = feature.properties
    bindAnchor(feature, findOne(anchor_id))
    bindLocalCategories(
      feature,
      sanitizedFeatures(local_category_ids.map(findOne))
    )
    bindGroups(feature, sanitizedFeatures(group_ids.map(findOne)))
    bindKiosk(feature, findOne(kiosk_id))
    bindPromotions(feature, sanitizedFeatures(promotion_ids.map(findOne)))
    bindPrivileges(feature, sanitizedFeatures(privilege_ids.map(findOne)))
    bindVenue(feature, findOne(venue_id))

    // TODO: bindCorrelation(feature)
  })

  // Prepare Openings
  openings.forEach((feature) => {
    const { level_id, venue_id } = feature.properties
    const level = findOne(level_id)
    const venue = findOne(venue_id)
    bindLevel(feature, level)
    bindUnit(feature, venue_id)
    bindVenue(feature, venue)
  })

  // Prepare Relationships
  relationships.forEach((feature) => {
    const { origin, destination, intermediary } = feature.properties
    bindOrigin(feature, findOne(origin.id))
    bindDestination(feature, findOne(destination?.id))
    bindIntermediary(
      feature,
      sanitizedFeatures(intermediary?.map((feature) => findOne(feature.id)))
    )
  })

  /**
   * Non- imdf
   */

  // Prepare Events
  events.forEach((feature) => {
    const { feature_id, venue_id } = feature.properties
    const location = findOne(feature_id)
    const venue = findOne(venue_id)
    bindFeature(feature, location)
    bindVenue(feature, venue)
    try {
      if (!_.isNil(feature_id) && _.isNil(_.get(feature, "properties.feature")))
        throw new Error(
          `Unable to find a feature data (${feature_id}) to binding a event's feature id (${feature?.id}).`
        )
    } catch (error) {
      sendCaptureExceptionToSentry({
        error,
        level: "error",
        transactionName: `Unable to find a feature data.`,
      })
    }
  })

  // Prepare Promotion
  promotions.forEach((feature) => {
    const { feature_id, venue_id } = feature.properties
    const location = findOne(feature_id)
    const venue = findOne(venue_id)
    bindFeature(feature, location)
    bindVenue(feature, venue)
  })

  // Prepare Promotion
  privileges.forEach((feature) => {
    const { occupant_id, venue_id } = feature.properties
    const occupant = findOne(occupant_id)
    const venue = findOne(venue_id)
    bindOccupant(feature, occupant)
    bindVenue(feature, venue)
  })
}
